import React, { useContext, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { skipToken } from "@reduxjs/toolkit/query";
import { TradeData, TradeDataWithKey } from "@shared/types";
import SocketPnlProvider from "@shared/features/dashboard/SocketPnlProvider";
import useCardsListSelector from "@shared/common/CardsList/useCardsListSelector.tsx";
import { CardsSelectorContext } from "@shared/common/CardsList/CardsSelector.context";
import { TradeActionsContext } from "@shared/features/trades/common/contexts.tsx";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context.tsx";
import usePagination from "common/Pagination/usePagination";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import { openMessage } from "utils/misc.tsx";
import { NewLookCardsGrid } from "features/portfolio/NewLook/styles.ts";
import PhoneTradeRow, {
  PhoneTradeRowSkeleton,
} from "features/trades/NewLook/PhoneTradeRow.tsx";
import useExpandedTrade from "features/trades/NewLook/useExpandedTrade.ts";
import { NEW_LOOK_TRADES_CARDS_GRID_ID } from "testIds";

const PAGE_SIZE = 12;
const CARD_MIN_WIDTH = 340;
const SKELETON_KEYS = ["first", "second", "third"];
const SPINNER_MARGIN_TOP = 40;

interface NewLookTradesCardsGridProps {
  data: TradeDataWithKey[];
  loading: boolean;
  isOpenTrades: boolean;
  emptyState?: React.JSX.Element;
  allowTradeClick?: boolean;
  allowBulkActions?: boolean;
  hideActions?: boolean;
  shouldFetchSocketPnl?: boolean;
}

type TradeCardOptions = Pick<
  NewLookTradesCardsGridProps,
  | "isOpenTrades"
  | "allowTradeClick"
  | "allowBulkActions"
  | "hideActions"
  | "shouldFetchSocketPnl"
>;

interface TradeCardProps extends TradeCardOptions {
  trade: TradeDataWithKey;
  expanded: boolean;
  onToggleExpanded: (tradeId: number) => void;
}

interface TradeCardsItemsProps extends TradeCardOptions {
  trades: TradeDataWithKey[];
  isPending: boolean;
}

interface TradesCardsProps extends TradeCardsItemsProps {
  loading: boolean;
}

const NewLookTradesCardsGrid = (props: NewLookTradesCardsGridProps) => {
  const { data, loading, emptyState, ...cardOptions } = props;
  const [pagination, trades] = usePagination({ data, pageSize: PAGE_SIZE });
  const cardsSelector = useTradesCardsSelector(data);
  if (!loading && !data.length) return emptyState ?? null;
  const selector = cardOptions.allowBulkActions ? cardsSelector : undefined;
  return (
    <SocketPnlProvider getTradesArgs={skipToken} openTradesData={trades}>
      <CardsSelectorContext.Provider value={selector}>
        <TradesCards
          trades={trades}
          loading={loading}
          isPending={loading && !data.length}
          {...cardOptions}
        />
        <PaginationRow>{pagination}</PaginationRow>
      </CardsSelectorContext.Provider>
    </SocketPnlProvider>
  );
};

export default NewLookTradesCardsGrid;

const TradesCards = ({ loading, isPending, ...items }: TradesCardsProps) => {
  const theme = useTheme();
  return (
    <LoadingSpinnerOverlay
      isLoading={loading && !isPending}
      wrap={true}
      spinnerMarginTop={SPINNER_MARGIN_TOP}
      backgroundColor={getNewLookTokens(theme).canvas}
    >
      <NewLookCardsGrid
        $minWidth={CARD_MIN_WIDTH}
        data-testid={NEW_LOOK_TRADES_CARDS_GRID_ID}
      >
        <TradeCardsItems isPending={isPending} {...items} />
      </NewLookCardsGrid>
    </LoadingSpinnerOverlay>
  );
};

const TradeCardsItems = ({
  trades,
  isPending,
  ...cardOptions
}: TradeCardsItemsProps) => {
  const { expandedTradeId, toggleExpanded } = useExpandedTrade();
  if (isPending)
    return SKELETON_KEYS.map((key) => (
      <PhoneTradeRowSkeleton key={key} inset={false} />
    ));
  return trades.map((trade) => (
    <TradeCard
      key={trade.id}
      trade={trade}
      expanded={expandedTradeId === trade.id}
      onToggleExpanded={toggleExpanded}
      {...cardOptions}
    />
  ));
};

const TradeCard = ({
  trade,
  allowBulkActions,
  shouldFetchSocketPnl = true,
  ...options
}: TradeCardProps) => (
  <PhoneTradeRow
    tradeData={trade}
    shouldFetchSocketPnl={shouldFetchSocketPnl}
    showBulkActions={allowBulkActions}
    inset={false}
    {...options}
  />
);

const useTradesCardsSelector = (data: TradeDataWithKey[]) => {
  const {
    selectedTradesIDs,
    setSelectedTradesIDs,
    getRowSelectable,
    isBulkMode,
    setIsBulkMode,
  } = useContext(TradeActionsContext) || {};
  const { bulkActionTrades } = useContext(TradesPageContext)!;
  const { getCheckboxProps, onSelectRow } = useCardsListSelector<
    TradeData,
    number
  >({
    selectedRows: selectedTradesIDs || [],
    setSelectedRows: setSelectedTradesIDs,
    getKey: (tradeData: TradeData) => tradeData.id,
    allRows: bulkActionTrades,
    filteredRows: data,
    getRowSelectable,
    openMessage,
  });

  return useMemo(() => {
    if (!setIsBulkMode) return undefined;
    return {
      isEditMode: !!isBulkMode,
      setIsEditMode: (tradeData?: TradeData) => {
        setIsBulkMode(true);
        if (tradeData) onSelectRow(tradeData, true);
      },
      getCheckboxProps,
    };
  }, [isBulkMode, getCheckboxProps, setIsBulkMode, onSelectRow]);
};

const PaginationRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  &:empty {
    display: none;
  }
`;
